import { Outlet } from 'react-router-dom';
import { useParams } from 'react-router';

// material-ui
import { styled, useTheme } from '@mui/material/styles';
import { AppBar, Box, CssBaseline, Toolbar, useMediaQuery } from '@mui/material';

// project imports
import { useMenu } from 'context/menu-context';
import Header from '../shared/Header';
import Sidebar from '../shared/Sidebar';
import WorkFlowMenuList from './WorkflowMenuList';
import { drawerWidth } from 'utils/constant';
import { SET_MENU } from 'utils/actions';
import { useDetailedWorkflow } from 'hooks/workflow/los.hooks';
import { WorkflowProvider } from 'context/workflow-context';

// styles
const Main = styled('main', { shouldForwardProp: prop => prop !== 'open' })(({ theme, open }) => ({
	...theme.typography.mainContent,
	...(!open && {
		borderBottomLeftRadius: 0,
		borderBottomRightRadius: 0,
		transition: theme.transitions.create('margin', {
			easing: theme.transitions.easing.sharp,
			duration: theme.transitions.duration.leavingScreen,
		}),
		[theme.breakpoints.up('md')]: {
			marginLeft: -(drawerWidth - 10),
			width: `calc(100% - ${drawerWidth}px)`,
		},
		[theme.breakpoints.down('md')]: {
			marginLeft: '10px',
			width: `calc(100% - ${drawerWidth}px)`,
			padding: '12px',
		},
		[theme.breakpoints.down('sm')]: {
			marginLeft: '10px',
			width: `calc(100% - ${drawerWidth}px)`,
			padding: '12px',
			marginRight: '10px',
		},
	}),
	...(open && {
		transition: theme.transitions.create('margin', {
			easing: theme.transitions.easing.easeOut,
			duration: theme.transitions.duration.enteringScreen,
		}),
		marginLeft: 0,
		borderBottomLeftRadius: 0,
		borderBottomRightRadius: 0,
		width: `calc(100% - ${drawerWidth}px)`,
		[theme.breakpoints.down('md')]: {
			marginLeft: '10px',
		},
		[theme.breakpoints.down('sm')]: {
			marginLeft: '10px',
		},
	}),
}));

// ==============================|| WORKFLOW LAYOUT ||============================== //

const WorkflowLayout = () => {
	const theme = useTheme();
	const matchDownMd = useMediaQuery(theme.breakpoints.down('md'));
	// Handle left drawer
	const { state: customization, dispatch } = useMenu();
	const leftDrawerOpened = customization.opened;
	const handleLeftDrawerToggle = () => {
		dispatch({ type: SET_MENU, opened: !leftDrawerOpened });
	};

	const { workflowId } = useParams();
	const workflowQuery = useDetailedWorkflow(workflowId);

	return (
		<Box sx={{ display: 'flex' }}>
			<CssBaseline />
			{/* header */}
			<AppBar
				enableColorOnDark
				position="fixed"
				color="inherit"
				elevation={2}
				sx={{
					bgcolor: theme.palette.background.paper,
					transition: leftDrawerOpened ? theme.transitions.create('width') : 'none',
				}}
			>
				<Toolbar sx={{ padding: '6px' }}>
					<Header handleLeftDrawerToggle={handleLeftDrawerToggle} />
				</Toolbar>
			</AppBar>
			{/* drawer */}
			<WorkflowProvider workflow={workflowQuery}>
				<Sidebar drawerOpen={!matchDownMd ? leftDrawerOpened : !leftDrawerOpened} drawerToggle={handleLeftDrawerToggle}>
					<WorkFlowMenuList />
				</Sidebar>
				{/* main content */}
				<Main theme={theme} open={leftDrawerOpened}>
					<Outlet />
				</Main>
			</WorkflowProvider>
		</Box>
	);
};

export default WorkflowLayout;
